import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { 
  Search, Settings, Hash, TrendingUp, FileText, Layout as LayoutIcon, 
  Link as LinkIcon, BarChart3, Activity, Menu, X, ChevronRight, History,
  LayoutDashboard, Wrench, Shield, LogOut, User, BookOpen, CreditCard, Wand2, Clock, Rocket, Sparkles, ChefHat, Factory, Users, FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { useAuth } from "@/context/AuthContext";
import { CreditBalance } from "@/components/CreditBalance";
import { OnboardingChecklist, WelcomeModal } from "@/components/OnboardingWidgets";

const categories = [
  { id: "seo-audit", name: "SEO Audit", icon: Search, color: "green" },
  { id: "technical-seo", name: "Technical SEO", icon: Settings, color: "blue" },
  { id: "keyword-research", name: "Keyword Research", icon: Hash, color: "purple" },
  { id: "competitive-analysis", name: "Competitive Analysis", icon: TrendingUp, color: "orange" },
  { id: "content-creation", name: "Content Creation", icon: FileText, color: "pink" },
  { id: "on-page-optimization", name: "On-Page Optimization", icon: LayoutIcon, color: "cyan" },
  { id: "link-building", name: "Link Building", icon: LinkIcon, color: "yellow" },
  { id: "data-analysis", name: "Data Analysis", icon: BarChart3, color: "indigo" },
  { id: "keyword-tracking", name: "Keyword Tracking", icon: Activity, color: "emerald" },
  { id: "misc-projects", name: "Misc. Projects", icon: Wrench, color: "slate" },
];

const iconColors = {
  green: "text-green-500",
  blue: "text-blue-500",
  purple: "text-purple-500",
  orange: "text-orange-500",
  pink: "text-pink-500",
  cyan: "text-cyan-500",
  yellow: "text-yellow-500",
  indigo: "text-indigo-500",
  emerald: "text-emerald-500",
  slate: "text-slate-400",
  amber: "text-amber-500",
};

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const location = useLocation();
  const { user, login, logout, isAdmin, isToolManager } = useAuth();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };
  
  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Marketing view: logged-out visitors on "/" get the full-bleed landing page
  // (it brings its own nav + footer) — hide the app chrome entirely.
  const { loading: authLoading } = useAuth();
  if (!user && !authLoading && location.pathname === "/") {
    return (
      <div className="min-h-screen bg-zinc-950 overflow-y-auto">
        <Outlet />
        <CookieConsentBanner />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out lg:translate-x-0",
          "bg-zinc-900/80 backdrop-blur-xl border-r border-zinc-800",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-zinc-800">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Search className="w-4 h-4 text-green-500" />
              </div>
              <span className="font-heading font-bold text-lg tracking-tight text-white">
                SEO Toolkit
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-4 py-6">
            <nav className="space-y-2">
              <Link
                to="/"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive("/") && location.pathname === "/" 
                    ? "bg-green-500/10 text-green-500 border-l-2 border-green-500" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid="nav-home"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm font-medium">All Tools</span>
              </Link>

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  Categories
                </span>
              </div>

              {categories.map((category) => {
                const Icon = category.icon;
                const active = isActive(`/tools/${category.id}`);
                return (
                  <Link
                    key={category.id}
                    to={`/tools/${category.id}`}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group",
                      active 
                        ? "bg-green-500/10 text-green-500 border-l-2 border-green-500" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    )}
                    data-testid={`nav-${category.id}`}
                  >
                    <Icon className={cn("w-4 h-4", active ? "text-green-500" : iconColors[category.color])} />
                    <span className="text-sm font-medium flex-1">{category.name}</span>
                    <ChevronRight className={cn(
                      "w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity",
                      active && "opacity-100"
                    )} />
                  </Link>
                );
              })}

              {user?.email === "reporting@emerald.consulting" && (
                <Link
                  to="/tools/seo-team"
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group",
                    isActive("/tools/seo-team")
                      ? "bg-amber-500/10 text-amber-500 border-l-2 border-amber-500"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  )}
                  data-testid="nav-seo-team"
                >
                  <Users className={cn("w-4 h-4", isActive("/tools/seo-team") ? "text-amber-500" : "text-amber-500")} />
                  <span className="text-sm font-medium flex-1">SEO Team</span>
                  <ChevronRight className={cn(
                    "w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity",
                    isActive("/tools/seo-team") && "opacity-100"
                  )} />
                </Link>
              )}

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  Dashboards
                </span>
              </div>

              <Link
                to="/dashboard/rank-tracker"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive("/dashboard/rank-tracker")
                    ? "bg-emerald-500/10 text-emerald-500 border-l-2 border-emerald-500" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid="nav-rank-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="text-sm font-medium">Rank Tracker</span>
              </Link>

              <Link
                to="/dashboard/search-console"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive("/dashboard/search-console")
                    ? "bg-blue-500/10 text-blue-500 border-l-2 border-blue-500" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid="nav-gsc-dashboard"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm font-medium">Search Console</span>
              </Link>


              {(isAdmin || isToolManager) && (
                <>
                  <div className="pt-4 pb-2">
                    <span className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                      Content Machine
                    </span>
                  </div>
                  <Link
                    to="/content-machine"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      isActive("/content-machine")
                        ? "bg-violet-500/10 text-violet-500 border-l-2 border-violet-500" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    )}
                    data-testid="nav-content-machine"
                    title="Single-recipe workflow — work one recipe end-to-end"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">Single-Recipe Workflow</span>
                  </Link>
                  <Link
                    to="/recipe-prioritizer"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      isActive("/recipe-prioritizer")
                        ? "bg-violet-500/10 text-violet-500 border-l-2 border-violet-500" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    )}
                    data-testid="nav-recipe-prioritizer"
                  >
                    <ChefHat className="w-4 h-4" />
                    <span className="text-sm font-medium">Recipe Prioritizer</span>
                  </Link>
                  <Link
                    to="/production-pipeline"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      isActive("/production-pipeline")
                        ? "bg-violet-500/10 text-violet-500 border-l-2 border-violet-500" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    )}
                    data-testid="nav-production-pipeline"
                    title="Batch workflow — process many recipes together (Keywords, Editorial QA, Briefs, Articles, Images, Publish)"
                  >
                    <Factory className="w-4 h-4" />
                    <span className="text-sm font-medium">Batch Pipeline</span>
                  </Link>
                </>
              )}

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  History
                </span>
              </div>

              <Link
                to="/results"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive("/results")
                    ? "bg-green-500/10 text-green-500 border-l-2 border-green-500" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid="nav-results"
              >
                <History className="w-4 h-4" />
                <span className="text-sm font-medium">Saved Results</span>
              </Link>

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  Learn
                </span>
              </div>

              <Link
                to="/resources"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive("/resources")
                    ? "bg-emerald-500/10 text-emerald-500 border-l-2 border-emerald-500" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid="nav-resources"
              >
                <BookOpen className="w-4 h-4" />
                <span className="text-sm font-medium">Resources & Guides</span>
              </Link>

              <Link
                to="/getting-started"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive("/getting-started")
                    ? "bg-green-500/10 text-green-500 border-l-2 border-green-500" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid="nav-getting-started"
              >
                <Rocket className="w-4 h-4" />
                <span className="text-sm font-medium">Getting Started</span>
              </Link>

              {/* Admin Link */}
              {isAdmin && (
                <>
                  <div className="pt-4 pb-2">
                    <span className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                      Admin
                    </span>
                  </div>
                  <Link
                    to="/admin"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      isActive("/admin")
                        ? "bg-purple-500/10 text-purple-500 border-l-2 border-purple-500" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    )}
                    data-testid="nav-admin"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">Admin Dashboard</span>
                  </Link>
                  {user?.email === "reporting@emerald.consulting" && (
                    <Link
                      to="/seo-services"
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                        isActive("/seo-services")
                          ? "bg-emerald-500/10 text-emerald-500 border-l-2 border-emerald-500"
                          : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                      )}
                      data-testid="nav-seo-services"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      <span className="text-sm font-medium">SEO Services Proposal</span>
                    </Link>
                  )}
                </>
              )}
            </nav>
          </ScrollArea>

          {/* User Profile & Footer */}
          <div className="p-4 border-t border-zinc-800 space-y-3">
            {user && !checklistDismissed && (
              <OnboardingChecklist onDismiss={() => setChecklistDismissed(true)} />
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-emerald-500/20 text-emerald-500 text-xs">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-white truncate">{user.name}</p>
                      <p className="text-xs text-zinc-500 truncate">{user.email}</p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
                  <DropdownMenuItem className="text-zinc-400">
                    <User className="w-4 h-4 mr-2" />
                    {user.email}
                  </DropdownMenuItem>
                  <Link to="/account">
                    <DropdownMenuItem className="text-zinc-300 cursor-pointer" data-testid="dropdown-account">
                      <CreditCard className="w-4 h-4 mr-2" />
                      My Account
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/custom-tools">
                    <DropdownMenuItem className="text-emerald-400 cursor-pointer" data-testid="dropdown-custom-tools">
                      <Wand2 className="w-4 h-4 mr-2" />
                      Custom Tools
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/history">
                    <DropdownMenuItem className="text-zinc-300 cursor-pointer" data-testid="dropdown-history">
                      <Clock className="w-4 h-4 mr-2" />
                      Tool History
                    </DropdownMenuItem>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin">
                      <DropdownMenuItem className="text-purple-400 cursor-pointer" data-testid="dropdown-admin">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </Link>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem 
                    className="text-red-400 cursor-pointer"
                    onClick={logout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={login}
                className="w-full flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 transition-colors text-left"
                data-testid="sidebar-sign-in"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-emerald-400"><User className="w-4 h-4" /> Sign up free</span>
                <span className="text-[11px] text-zinc-500 pl-6">Starter credits + saved history</span>
              </button>
            )}
            <div className="px-3 py-2 rounded-lg bg-zinc-800/30">
              <p className="text-xs text-zinc-600 text-center">Powered by Emerald Consulting</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-md flex items-center px-6 sticky top-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-4 text-zinc-400 hover:text-white"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <CreditBalance />
            <Link to="/pricing" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors hidden sm:block" data-testid="nav-pricing">
              Pricing
            </Link>
            {!user && (
              <Button
                variant="outline"
                size="sm"
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-8"
                onClick={login}
                data-testid="header-sign-in"
              >
                <User className="w-3.5 h-3.5 mr-1.5" />
                Sign In
              </Button>
            )}
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">
          <Outlet />

          {/* Footer */}
          <footer className="border-t border-zinc-800 px-6 py-6 mt-auto">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} Emerald Consulting. All rights reserved.</p>
              <nav className="flex items-center gap-4" data-testid="footer-legal-links">
                <Link to="/terms" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">Terms</Link>
                <Link to="/privacy" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">Privacy</Link>
                <Link to="/cookies" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">Cookies</Link>
                <a href="mailto:derek@emerald.consulting" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">Contact</a>
              </nav>
            </div>
          </footer>
        </div>
      </main>
      {user && <WelcomeModal />}
      <CookieConsentBanner />
    </div>
  );
}
